import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button, Checkbox, Modal, Icon, Title, Text,
} from '@nimbus-ds/components'
import { PlusIcon } from '@nimbus-ds/icons'
import { ExperimentWizard } from '@/components/experiments/ExperimentWizard'
import { ExperimentsList } from '@/components/experiments/ExperimentsList'
import { useToast } from '@/hooks/use-toast'
import type { Experiment, ExperimentFormData, RiskLevel } from '@/types'
import { experimentService } from '@/api/experiments'
import { authService } from '@/api/auth'
import { riskService } from '@/api/risk'
import { createMediaViewEventsForInsertions } from '@/lib/mediaViewEvents'

export function ExperimentsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [showWizard, setShowWizard] = useState(false)
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    experimentId: string
    experimentName: string
  }>({
    open: false,
    experimentId: '',
    experimentName: ''
  })

  // Store-level default decision posture — pre-selects the wizard's risk control.
  const [defaultRiskLevel, setDefaultRiskLevel] = useState<RiskLevel>('balanced')

  useEffect(() => {
    loadExperiments()
    const storeId = authService.getCurrentStoreId()
    if (storeId) {
      riskService.getConfig(storeId).then(setDefaultRiskLevel)
    }
  }, [])

  const loadExperiments = async () => {
    try {
      setLoading(true)
      // Get store ID from localStorage
      const userInfo = localStorage.getItem('tnx_user_info')
      const storeId = userInfo ? JSON.parse(userInfo).store_id : null
      
      if (storeId) {
        const data = await experimentService.getExperiments()
        // Show newest first
        const sorted = [...data].sort((a, b) => {
          const aDate = new Date(a.created_at || a.updated_at || 0).getTime()
          const bDate = new Date(b.created_at || b.updated_at || 0).getTime()
          return bDate - aDate
        })
        setExperiments(sorted)
      }
    } catch (error) {
      console.error('Failed to load experiments:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExperimentCreate = async (experimentData: ExperimentFormData) => {
    try {
      console.log('Creating experiment:', experimentData)
      console.log('variantConfig captured_events:', (experimentData.variantConfig as any)?.captured_events)
      console.log('Form data received:', {
        enableStartTime: experimentData.enableStartTime,
        startDateTime: experimentData.startDateTime,
        enableEndTime: experimentData.enableEndTime,
        endDateTime: experimentData.endDateTime,
        enableSessionLimit: experimentData.enableSessionLimit,
        sessionLimit: experimentData.sessionLimit
      })

      // 1) Create experiment with full configuration
      const deriveTier = (_type: string): number => 1

      // Build variants and traffic_split: multi-variant if variantsList exists, else 2-variant
      let variants: Record<string, any>
      let traffic_split: Record<string, number>

      if (experimentData.variantsList && Object.keys(experimentData.variantsList).length > 0) {
        // Multi-variant mode (from MultiVariantSplit component)
        variants = {}
        traffic_split = {}
        for (const [key, v] of Object.entries(experimentData.variantsList)) {
          traffic_split[key] = v.trafficPct
          variants[key] = {
            name: v.name,
            description: key === 'control' ? 'Control' : v.name,
            config: key === 'control' ? {} : (experimentData.variantConfig || {}),
          }
        }
      } else {
        // Legacy 2-variant mode
        variants = {
          control: { name: 'Control', description: 'Control', config: {} },
          treatment: {
            name: 'Variante',
            description: 'Tratamiento',
            config: experimentData.variantConfig || {},
          },
        }
        traffic_split = {
          control: experimentData.controlTraffic,
          treatment: experimentData.variantTraffic,
        }
      }

      const payload: any = {
        name: experimentData.name,
        hypothesis: experimentData.hypothesis,
        objective: experimentData.objective,
        type: experimentData.type, // Always tier1_visual (default in wizard)
        tier: deriveTier(experimentData.type),
        is_negative: experimentData.isNegative === true,
        mde: experimentData.mde ?? null, // §8.1 relative-% MDE → BB+ROPE band on results
        primary_segment: experimentData.primarySegment || 'overall', // §8.3 confirmatory segment
        risk_level: experimentData.riskLevel || defaultRiskLevel, // decision posture (RISK-LEVELS-COPY.md)
        hard_to_revert: experimentData.hardToRevert === true,
        traffic_split,
        targeting: {
          url_patterns: experimentData.urlPatterns,
          device_conditions: experimentData.devices,
          exclusions: experimentData.exclusions,
        },
        variants,
        funnel_config: {
          use_default: experimentData.funnelOption === 'default',
          selected_steps: experimentData.funnelOption === 'custom' ? experimentData.funnelSteps : [],
          custom_steps: [],
        },
        goals: experimentData.goals || null,
      }

      // EXP_TIMING_001: Add timing and session_limit if provided
      // Build timing object if any timing is enabled with actual values
      const timing: any = {}
      if (experimentData.enableStartTime && experimentData.startDateTime) {
        timing.start_date_time = new Date(experimentData.startDateTime).toISOString()
      }
      if (experimentData.enableEndTime && experimentData.endDateTime) {
        timing.end_date_time = new Date(experimentData.endDateTime).toISOString()
      }
      // Only add timing if we have at least one datetime
      if (Object.keys(timing).length > 0) {
        payload.timing = timing
      }

      // Add session limit if enabled
      if (experimentData.enableSessionLimit && experimentData.sessionLimit) {
        payload.session_limit = experimentData.sessionLimit
      }

      console.log('API Payload (with tier):', payload)
      console.log('Payload variants.treatment.config:', payload.variants.treatment.config)
      console.log('Payload captured_events:', payload.variants.treatment.config?.captured_events)
      const created = await experimentService.createExperiment(payload)

      // 2) Activate experiment (to mimic "Launch")
      await experimentService.updateExperiment(created.id, { status: 'active' } as any)

      // Phase 4: Check if draft events were created from Visual Editor
      const draftEventIds = (created as any).draft_event_ids || []
      const hasDraftEvents = draftEventIds.length > 0

      // Create media_view custom events for visual-editor insertions flagged
      // "track visibility". Done here — after the experiment exists — so each
      // event can be linked via experiment_id. Non-fatal.
      let mediaEventsCreated = 0
      try {
        const storeId = authService.getCurrentStoreId()
        if (storeId) {
          const mediaResult = await createMediaViewEventsForInsertions(
            experimentData.variantConfig,
            created.id,
            storeId,
          )
          mediaEventsCreated = mediaResult.created
          if (mediaResult.failed > 0) {
            console.warn(`[TNX] ${mediaResult.failed} media_view event(s) failed to create`)
          }
        }
      } catch (mediaErr) {
        console.error('[TNX] media_view event creation failed (non-fatal):', mediaErr)
      }

      let createdDescription = hasDraftEvents
        ? `Tu experimento fue creado exitosamente con ${draftEventIds.length} evento(s) en borrador. Ve a Eventos Personalizados → Filtrar por Borrador para activarlos.`
        : 'Tu experimento fue creado y lanzado exitosamente.'
      if (mediaEventsCreated > 0) {
        createdDescription += ` Se ${mediaEventsCreated === 1 ? 'creó' : 'crearon'} ${mediaEventsCreated} evento(s) de visibilidad de imagen.`
      }

      toast({
        title: 'Experimento creado',
        description: createdDescription,
      })

      // Refresh list and close wizard
      await loadExperiments()
      setShowWizard(false)
    } catch (error) {
      console.error('Failed to create experiment:', error)
      toast({
        title: 'Error',
        description: 'No se pudo crear el experimento. Inténtalo de nuevo.',
        variant: 'destructive',
      })
    }
  }

  const handleExperimentAction = async (action: string, experimentId: string) => {
    try {
      setActionLoading(experimentId)

      switch (action) {
        case 'start':
          await experimentService.updateExperiment(experimentId, { status: 'active' })
          toast({
            title: 'Experimento iniciado',
            description: 'El experimento se ha activado exitosamente.',
          })
          break

        case 'pause':
          await experimentService.pauseExperiment(experimentId)
          toast({
            title: 'Experimento pausado',
            description: 'El experimento se ha pausado exitosamente.',
          })
          break

        case 'resume':
          await experimentService.resumeExperiment(experimentId)
          toast({
            title: 'Experimento reanudado',
            description: 'El experimento se ha reanudado exitosamente.',
          })
          break

        case 'complete':
          await experimentService.updateExperiment(experimentId, { status: 'completed' })
          toast({
            title: 'Experimento finalizado',
            description: 'El experimento se ha marcado como completado.',
          })
          break

        case 'view':
          navigate(`/dashboard/experiments/${experimentId}`)
          return

        case 'edit':
          navigate(`/dashboard/experiments/${experimentId}/edit`)
          return

        case 'results':
          navigate(`/dashboard/experiments/${experimentId}/results`)
          return

        case 'archive':
          await experimentService.updateExperiment(experimentId, { status: 'archived' })
          toast({
            title: 'Experimento archivado',
            description: 'El experimento se ha archivado exitosamente.',
          })
          break

        case 'delete': {
          const experiment = experiments.find(e => e.id === experimentId)
          if (experiment) {
            setDeleteDialog({
              open: true,
              experimentId,
              experimentName: experiment.name
            })
          }
          return
        }

        default:
          console.warn('Unknown action:', action)
          return
      }

      // Reload experiments list after status changes
      await loadExperiments()
    } catch (error) {
      console.error(`Failed to ${action} experiment:`, error)
      toast({
        title: 'Error',
        description: `No se pudo ${action === 'start' ? 'iniciar' : action === 'pause' ? 'pausar' : action === 'complete' ? 'finalizar' : action === 'archive' ? 'archivar' : 'reanudar'} el experimento. Inténtalo de nuevo.`,
        variant: 'destructive',
      })
    } finally {
      setActionLoading(null)
    }
  }

  const handleDeleteConfirm = async () => {
    try {
      setActionLoading(deleteDialog.experimentId)
      await experimentService.deleteExperiment(deleteDialog.experimentId)
      
      toast({
        title: 'Experimento eliminado',
        description: 'El experimento se ha eliminado exitosamente.',
      })

      // Close dialog and reload experiments
      setDeleteDialog({ open: false, experimentId: '', experimentName: '' })
      await loadExperiments()
    } catch (error) {
      console.error('Failed to delete experiment:', error)
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el experimento. Inténtalo de nuevo.',
        variant: 'destructive',
      })
    } finally {
      setActionLoading(null)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Title as="h1">Experimentos</Title>
          <Text>
            Gestiona tus experimentos A/B y tests multivariado
          </Text>
        </div>
        <Button appearance="primary" onClick={() => setShowWizard(true)}>
          <Icon source={<PlusIcon />} color="currentColor" />
          Nuevo Experimento
        </Button>
      </div>

      {/* Filters */}
      {experiments.some(e => e.status === 'archived') && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="show-archived"
            name="show-archived"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            label="Incluir archivados"
          />
        </div>
      )}

      {/* Experiments List */}
      <ExperimentsList
        experiments={showArchived ? experiments : experiments.filter(e => e.status !== 'archived')}
        onExperimentAction={handleExperimentAction}
        loading={loading}
        actionLoading={actionLoading}
      />

      {/* Experiment Creation Wizard */}
      <ExperimentWizard
        open={showWizard}
        onOpenChange={setShowWizard}
        onExperimentCreate={handleExperimentCreate}
        defaultRiskLevel={defaultRiskLevel}
      />

      {/* Delete Confirmation Dialog */}
      <Modal
        open={deleteDialog.open}
        onDismiss={() => setDeleteDialog(prev => ({ ...prev, open: false }))}
      >
        <Modal.Header title="¿Eliminar experimento?" />
        <Modal.Body>
          <Text>
            Estás a punto de eliminar el experimento "{deleteDialog.experimentName}".
            Esta acción no se puede deshacer y se perderán todos los datos del experimento.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="neutral"
            onClick={() => setDeleteDialog(prev => ({ ...prev, open: false }))}
          >
            Cancelar
          </Button>
          <Button
            appearance="danger"
            onClick={handleDeleteConfirm}
            disabled={actionLoading === deleteDialog.experimentId}
          >
            {actionLoading === deleteDialog.experimentId ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
